import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, LogOut, X, Loader2, Image as ImageIcon, Inbox } from "lucide-react";
import api from "../lib/api";
import { Logo } from "../components/Logo";
import { PORTFOLIO_CATEGORIES } from "../data/site";

const CATS = PORTFOLIO_CATEGORIES.filter((c) => c !== "All");
const cls = "w-full bg-ink-900 border border-line text-white placeholder-white/35 focus:border-orange focus:outline-none focus:ring-1 focus:ring-orange px-4 py-3";
const emptyP = { title: "", category: CATS[0], description: "", image_url: "", featured: false };

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyP);
  const [saving, setSaving] = useState(false);
  const [viewer, setViewer] = useState(null);

  const load = async () => {
    try {
      const [p, q] = await Promise.all([api.get("/projects"), api.get("/quotes")]);
      setProjects(p.data);
      setQuotes(q.data);
    } catch (e) {
      if (e.response?.status === 401) { localStorage.removeItem("aus_token"); nav("/admin/login"); }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("aus_token")) { nav("/admin/login"); return; }
    api.get("/auth/me").then(load).catch(() => nav("/admin/login"));
  }, []);

  const logout = () => { localStorage.removeItem("aus_token"); nav("/admin/login"); };

  const openNew = () => { setForm(emptyP); setEditing("new"); };
  const openEdit = (p) => { setForm({ title: p.title, category: p.category, description: p.description || "", image_url: p.image_url, featured: p.featured }); setEditing(p.id); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image_url) { toast.error("Title and image URL are required."); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/projects", form);
      else await api.put(`/projects/${editing}`, form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Deleted");
    load();
  };

  const delQuote = async (id) => {
    if (!window.confirm("Delete this quote request?")) return;
    await api.delete(`/quotes/${id}`);
    load();
  };

  return (
    <div className="min-h-screen bg-ink-900">
      <header className="border-b border-line bg-ink-900/85 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-5 md:px-8 h-16 flex items-center justify-between">
          <Logo onClick={() => nav("/")} />
          <button data-testid="logout-btn" onClick={logout} className="flex items-center gap-2 text-sm font-head uppercase tracking-wider text-white/70 hover:text-orange transition-colors">
            <LogOut size={16} /> Logout
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 md:px-8 py-8">
        <div className="flex items-center gap-2 mb-8">
          {[["projects", "Projects", projects.length], ["quotes", "Quote Requests", quotes.length]].map(([k, label, n]) => (
            <button key={k} data-testid={`tab-${k}`} onClick={() => setTab(k)}
              className={`font-head text-sm uppercase tracking-wider px-5 py-2.5 border transition-colors ${tab === k ? "bg-orange border-orange text-white" : "border-line text-white/60 hover:text-white"}`}>
              {label} <span className="opacity-60">({n})</span>
            </button>
          ))}
        </div>

        {tab === "projects" && (
          <>
            <div className="flex justify-between items-center mb-5">
              <h1 className="font-head font-black uppercase tracking-tight text-2xl">Portfolio Projects</h1>
              <button data-testid="add-project-btn" onClick={openNew} className="flex items-center gap-2 bg-orange hover:bg-orange-hover text-white font-head font-bold uppercase text-sm tracking-wide px-5 py-2.5 transition-colors">
                <Plus size={18} /> Add Project
              </button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((p) => (
                <div key={p.id} data-testid={`admin-project-${p.id}`} className="border border-line bg-ink-800 overflow-hidden">
                  <div className="h-40 bg-ink-700"><img src={p.image_url} alt={p.title} className="w-full h-full object-cover" /></div>
                  <div className="p-4">
                    <span className="overline text-[0.6rem]">{p.category}</span>
                    <h3 className="font-head font-bold uppercase text-sm mt-1">{p.title}</h3>
                    <div className="mt-3 flex gap-2">
                      <button data-testid={`edit-${p.id}`} onClick={() => openEdit(p)} className="flex-1 flex items-center justify-center gap-1 border border-line hover:border-orange text-white/80 text-xs uppercase py-2 transition-colors"><Pencil size={13} /> Edit</button>
                      <button data-testid={`delete-${p.id}`} onClick={() => del(p.id)} className="flex items-center justify-center border border-line hover:border-orange hover:text-orange text-white/60 px-3 py-2 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "quotes" && (
          <>
            <h1 className="font-head font-black uppercase tracking-tight text-2xl mb-5">Quote Requests</h1>
            {quotes.length === 0 && <div className="border border-line bg-ink-800 p-12 text-center text-white/40"><Inbox className="mx-auto mb-3" /> No quote requests yet.</div>}
            <div className="space-y-3">
              {quotes.map((q) => (
                <div key={q.id} data-testid={`quote-${q.id}`} className="border border-line bg-ink-800 p-5">
                  <div className="flex justify-between items-start gap-4">
                    <div>
                      <h3 className="font-head font-bold text-lg">{q.name} <span className="text-white/40 text-sm font-body">· {q.business_name || "—"}</span></h3>
                      <div className="mt-1 text-sm text-white/60 flex flex-wrap gap-x-4">
                        <a href={`tel:${q.phone}`} className="hover:text-orange">{q.phone}</a>
                        <a href={`mailto:${q.email}`} className="hover:text-orange">{q.email}</a>
                      </div>
                    </div>
                    <button data-testid={`delete-quote-${q.id}`} onClick={() => delQuote(q.id)} className="text-white/40 hover:text-orange"><Trash2 size={16} /></button>
                  </div>
                  <div className="mt-3 grid sm:grid-cols-3 gap-2 text-xs text-white/60">
                    <div><span className="text-white/35 uppercase tracking-wider">Signage:</span> {q.signage_type || "—"}</div>
                    <div><span className="text-white/35 uppercase tracking-wider">Vehicle:</span> {q.vehicle_type || "—"}</div>
                    <div><span className="text-white/35 uppercase tracking-wider">Qty:</span> {q.num_vehicles || "—"}</div>
                  </div>
                  {q.message && <p className="mt-3 text-sm text-white/70 border-l-2 border-orange pl-3">{q.message}</p>}
                  {q.photos?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {q.photos.map((src, i) => (
                        <button
                          key={i}
                          type="button"
                          data-testid={`quote-photo-${q.id}-${i}`}
                          onClick={() => setViewer(src)}
                          className="group relative h-16 w-16 border border-line overflow-hidden hover:border-orange transition-colors"
                        >
                          <img src={src} alt="attachment" className="h-full w-full object-cover" />
                          <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                            <ImageIcon size={16} className="text-white" />
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-5" onClick={() => setEditing(null)}>
          <div className="w-full max-w-lg border border-line bg-ink-800" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center border-b border-line p-5">
              <h2 className="font-head font-bold uppercase">{editing === "new" ? "Add Project" : "Edit Project"}</h2>
              <button onClick={() => setEditing(null)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="p-5 space-y-4" data-testid="project-form">
              <input data-testid="project-title" className={cls} placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <select data-testid="project-category" className={cls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="project-image" className={cls} placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
              {form.image_url && <img src={form.image_url} alt="preview" className="w-full h-40 object-cover border border-line" />}
              <textarea data-testid="project-desc" rows={3} className={`${cls} resize-none`} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured project
              </label>
              <button data-testid="save-project" disabled={saving} className="w-full flex items-center justify-center gap-2 bg-orange hover:bg-orange-hover disabled:opacity-60 text-white font-head font-bold uppercase tracking-wide py-3 transition-colors">
                {saving && <Loader2 className="animate-spin" size={18} />} Save Project
              </button>
            </form>
          </div>
        </div>
      )}

      {viewer && (
        <div
          data-testid="photo-viewer"
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-5"
          onClick={() => setViewer(null)}
        >
          <button
            data-testid="photo-viewer-close"
            onClick={() => setViewer(null)}
            className="absolute top-5 right-5 text-white/70 hover:text-white"
          >
            <X size={28} />
          </button>
          <img
            src={viewer}
            alt="Quote attachment"
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[90vw] object-contain border border-line"
          />
          <a
            href={viewer}
            download="aussigns-attachment.jpg"
            onClick={(e) => e.stopPropagation()}
            data-testid="photo-viewer-download"
            className="absolute bottom-6 bg-orange hover:bg-orange-hover text-white font-head font-bold uppercase tracking-wide text-sm px-6 py-3 transition-colors"
          >
            Download
          </a>
        </div>
      )}
    </div>
  );
}
